import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import Select, {
  components,
  ControlProps,
  CSSObjectWithLabel,
  FormatOptionLabelMeta,
} from 'react-select';
import { IoMdCloseCircle } from 'react-icons/io';
import { MdArrowDropDown } from 'react-icons/md';
import { Flex } from '../styles/layout';
import { ParagraphSmallBold } from '../styles/typography';
import { ClearSelect } from 'app/pages/library/videos/videoList/components/style';
import { getControlStyles } from './Dropdown';

export type Option = {
  value: string | number;
  label: string | number;
  isFolderShared?: boolean;
  access?: boolean | string;
};

const MultiSelect = styled(Select)<{ width?: string }>`
  position: relative;
  display: block;
  font-size: 16px;
  height: 40px;
  box-sizing: border-box;
  width: ${props => props.width ?? 'auto'};
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  border: none;
  box-sizing: border-box;
  &:focus {
    color: #495057;
    background-color: ${({ theme }) => theme.colors.white['100']};
    outline: 0;
  }
`;

type Props = {
  value: any;
  options: Array<Option> | any;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: any, actionMeta?: any) => void;
  showIndicator?: boolean;
  isMulti?: any /* used as any because creatable select not considering it as boolean */;
  formatOptionLabel?:
    | ((option: any, labelMeta: FormatOptionLabelMeta<any>) => React.ReactNode)
    | undefined;
  width?: string;
};

type DropdownArrowProps = {
  menuIsOpen?: boolean;
};

export const DropdownArrowIcon = styled.span<DropdownArrowProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding-right: 12px;
  position: relative;
  svg {
    ${props => props.menuIsOpen && 'transform: rotate(180deg);'}
  }
`;

export const OptionWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
`;

export const OptionIconWrapper = styled.div`
  text-align: center;
`;

export const DropdownForFolderShare = (props: Props) => {
  const {
    options,
    value,
    placeholder = 'No Folders',
    showIndicator = true,
    disabled = false,
    onChange,
    formatOptionLabel,
    width = '100%',
  } = props;
  const themes = useTheme();
  const selectRef = React.useRef<any>(null);

  const DropdownIndicator = (props: any) => {
    return showIndicator ? (
      <DropdownArrowIcon menuIsOpen={props.selectProps.menuIsOpen}>
        <MdArrowDropDown size={18} opacity={0.5} />
      </DropdownArrowIcon>
    ) : (
      <></>
    );
  };

  const SingleValue = (props: any) => {
    const { children } = props;
    return (
      <components.SingleValue {...props}>
        <Flex
          alignItems='center'
          padding='0'
          borderRadius='3px'
          backgroundColor={themes.colors.primary['non_alpha']}
        >
          <ParagraphSmallBold>{children}</ParagraphSmallBold>
          <ClearSelect onClick={() => selectRef.current?.clearValue()}>
            <IoMdCloseCircle color={themes.colors.secondary[40]} size={13} />
          </ClearSelect>
        </Flex>
      </components.SingleValue>
    );
  };

  const MultiValueRemove = (props: any) => {
    return (
      <components.MultiValueRemove {...props}>
        <IoMdCloseCircle color={themes.colors.neutral[80]} size={13} />
      </components.MultiValueRemove>
    );
  };

  const MultiValue = (props: any) => {
    const { children } = props;

    return (
      <components.MultiValue {...props}>
        <Flex
          alignItems='center'
          padding='0'
          borderRadius='3px'
          backgroundColor={themes.colors.primary['non_alpha']}
        >
          <ParagraphSmallBold>{children}</ParagraphSmallBold>
        </Flex>
      </components.MultiValue>
    );
  };

  const selectStyle = {
    control: (
      base: CSSObjectWithLabel,
      props: ControlProps<Option, false>
    ): CSSObjectWithLabel => {
      return {
        ...base,
        boxShadow: 'none',
        borderRadius: 6,
        minHeight: '40px',
        cursor: 'pointer',
        ...getControlStyles(props, themes),
        '&:hover': {
          borderColor: themes.colors.primary[100],
        },
        '&:focus-within': {
          borderColor: themes.colors.primary[100],
        },
      };
    },
    option: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: '0px',
      backgroundColor: 'transparent',
      color: themes.colors.neutral[100],
      cursor: 'pointer',
      ':hover': {
        backgroundColor: themes.colors.secondary[5],
        opacity: '.8',
      },
      ':focus': {
        backgroundColor: 'transparent',
        opacity: '.8',
      },
    }),
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      position: 'static',
      width: 'auto',
      maxWidth: 'initial',
      transform: 'none',
    }),
    multiValue: (base: CSSObjectWithLabel) => ({
      ...base,
      position: 'static',
      width: 'auto',
      maxWidth: 'initial',
      transform: 'none',
      background: themes.colors.secondary[5],
      border: themes.colors.neutral[10],
      borderRadius: '6px',
      flex: 'none',
    }),
    multiValueRemove: (base: CSSObjectWithLabel) => ({
      ...base,
      ':hover': {
        backgroundColor: 'transparent',
      },
      ':focus': {
        backgroundColor: 'transparent',
      },
    }),
    indicatorSeparator: (base: CSSObjectWithLabel) => ({
      ...base,
      display: 'none',
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      display: 'flex',
      flexWrap: 'nowrap',
      overflowX: 'auto',
      maxWidth: '100%',
      padding: '4px',
      gap: '4px',
      scrollbarWidth: 'none',
    }),

    input: (base: CSSObjectWithLabel) => ({
      ...base,
      flex: 'none',
      width: 'auto',
      minWidth: '2px',
      whiteSpace: 'nowrap',
    }),
    menuList: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: '20px',
      gap: '10px',
      display: 'flex',
      flexDirection: 'column',
      border: '1px',
    }),
  };

  return (
    <MultiSelect
      width={width}
      ref={selectRef}
      components={{
        SingleValue,
        MultiValue,
        MultiValueRemove,
        IndicatorSeparator: () => null,
        DropdownIndicator: DropdownIndicator,
      }}
      styles={selectStyle as any}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      options={options}
      isMulti
      isDisabled={disabled}
      formatOptionLabel={formatOptionLabel}
    />
  );
};
